import { faInfoCircle } from "@fortawesome/sharp-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Tooltip, Typography } from "@mui/material"
import type { BiothreatSignal } from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import { useMemo } from "react"
import {
  isRelative,
  isSameLookback,
  type DynamicDateRange,
} from "../../../utils/dates"
import { extraColors } from "../../../utils/theme"
import { RELATIVE_DATERANGE_CHOICES } from "../../Shared/PHCDateRangePicker"
import { usePrecomputedBiothreatSignals } from "../PrecomputedBiothreatSignalsContext"
import { ALL_DISEASES, ALL_GEO, type PrecomputedTab } from "../types"
import {
  getNwssDetections,
  getNwssSites,
  severityCounts,
  statusCounts,
  V2_SEVERITY_MINS,
} from "./biothreatV2Signal"
import {
  V2_ACCENT,
  V2_EYEBROW,
  V2_NUMERIC,
  V2_SEVERITY_COLOR,
  V2_SEVERITY_LABEL,
  V2_SEVERITY_ORDER,
  V2_STATUS_COLOR,
  V2_STATUS_LABEL,
  V2_SURFACE,
  type V2NwssStatus,
} from "./biothreatV2Tokens"

const formatDateRange = (dateRange: DynamicDateRange): string => {
  if (isRelative(dateRange)) {
    const match = RELATIVE_DATERANGE_CHOICES.find(choice =>
      isSameLookback(choice, dateRange)
    )
    return match ? match.label : "Custom range"
  }
  const [start, end] = dateRange
  return start && end
    ? `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`
    : "Custom range"
}

/** Tooltip that explains a stat AND the filtered context behind its count. */
const StatTooltip: React.FC<{
  description: string
  context: string
  children: React.ReactElement
}> = ({ description, context, children }) => (
  <Tooltip
    arrow
    placement="top"
    title={
      <Box sx={{ p: 0.5 }}>
        <Box sx={{ fontWeight: 700, mb: 0.5 }}>{description}</Box>
        <Box sx={{ opacity: 0.85, fontSize: 11, lineHeight: 1.4 }}>
          Across all signals matching the filter · {context}
        </Box>
      </Box>
    }
  >
    {children}
  </Tooltip>
)

/** Small "i" affordance signalling the counts in a block have hover tooltips. */
const InfoHint: React.FC<{ context: string }> = ({ context }) => (
  <Tooltip
    arrow
    placement="top"
    title={
      <Box sx={{ p: 0.5, fontSize: 11, lineHeight: 1.4 }}>
        Hover any value for details · {context}
      </Box>
    }
  >
    <Box
      component="span"
      sx={{
        color: V2_ACCENT.cyan,
        opacity: 0.7,
        cursor: "help",
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "center",
      }}
    >
      <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: 12 }} />
    </Box>
  </Tooltip>
)

const Block: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 3,
      // Brief: aggregation-strip blocks 32px / 26px.
      px: "32px",
      py: "26px",
      borderRight: `1px solid ${V2_SURFACE.hairline}`,
    }}
  >
    {children}
  </Box>
)

const Eyebrow: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color,
}) => (
  <Typography sx={{ ...V2_EYEBROW, color: color ?? extraColors.translucent50 }}>
    {children}
  </Typography>
)

const Metric: React.FC<{ value: number; label: string }> = ({
  value,
  label,
}) => (
  <Box>
    <Typography
      sx={{
        ...V2_NUMERIC,
        fontSize: "1.5rem",
        fontWeight: 800,
        color: extraColors.white,
        lineHeight: 1,
      }}
    >
      {value.toLocaleString()}
    </Typography>
    <Eyebrow>{label}</Eyebrow>
  </Box>
)

const NWSS_STATUS_ORDER: V2NwssStatus[] = ["start", "ongoing", "ended"]

export const BiothreatV2AggregationStrip: React.FC<{
  rows: BiothreatSignal[]
  kind: PrecomputedTab
  mapOpen: boolean
  onToggleMap: () => void
}> = ({ rows, kind, mapOpen, onToggleMap }) => {
  const isGdelt = kind === "gdelt"
  const { gdeltFilters, nwssFilters, dateRange } =
    usePrecomputedBiothreatSignals()

  const sev = useMemo(() => severityCounts(rows), [rows])
  const status = useMemo(() => statusCounts(rows), [rows])

  // Human-readable summary of the active date range + applied filters, shown in
  // every aggregate tooltip so the counts' context is clear.
  const filterContext = useMemo(() => {
    const parts = [`Active date range: ${formatDateRange(dateRange)}`]
    if (isGdelt) {
      parts.push(
        `Disease: ${gdeltFilters.disease === ALL_DISEASES ? "All" : gdeltFilters.disease}`
      )
      if (gdeltFilters.country !== ALL_GEO) {
        parts.push(`Country: ${gdeltFilters.country}`)
      }
      if (gdeltFilters.state !== ALL_GEO) {
        parts.push(`US state: ${gdeltFilters.state}`)
      }
      if (gdeltFilters.impactScoreMin) {
        parts.push(`Min impact: ${gdeltFilters.impactScoreMin}`)
      }
      if (gdeltFilters.memberTitleSearch) {
        parts.push(`Title: "${gdeltFilters.memberTitleSearch}"`)
      }
      if (gdeltFilters.summarySearch) {
        parts.push(`Summary: "${gdeltFilters.summarySearch}"`)
      }
    } else {
      parts.push(
        `Disease: ${nwssFilters.disease === ALL_DISEASES ? "All" : nwssFilters.disease}`
      )
      parts.push(
        `State: ${nwssFilters.state === ALL_GEO ? "All" : nwssFilters.state}`
      )
    }
    return parts.join(" · ")
  }, [isGdelt, gdeltFilters, nwssFilters, dateRange])

  const metrics = useMemo(() => {
    if (isGdelt) {
      return [
        {
          value: rows.length,
          label: "Active clusters",
          description: "GDELT signal clusters in the loaded results",
        },
        {
          value: rows.reduce((sum, row) => sum + row.cluster_count, 0),
          label: "Articles",
          description: "Total source articles across those clusters",
        },
        {
          value: new Set(rows.map(row => row.resolved_location)).size,
          label: "Geographies",
          description: "Distinct locations across those clusters",
        },
      ]
    }
    return [
      {
        value: rows.length,
        label: "Active events",
        description: "Wastewater detection events in the loaded results",
      },
      {
        value: rows.reduce((sum, row) => sum + getNwssSites(row), 0),
        label: "Sites reporting",
        description: "Total reporting sites across those events",
      },
      {
        value: rows.reduce((sum, row) => sum + getNwssDetections(row), 0),
        label: "Detections",
        description: "Total detections across those events",
      },
    ]
  }, [isGdelt, rows])

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch",
        flexWrap: "wrap",
        borderBottom: `1px solid ${V2_SURFACE.hairlineStrong}`,
        backgroundColor: V2_SURFACE.panel,
      }}
    >
      {/* Scan-map show/hide control (replaces the threat-condition gauge). */}
      <Block>
        <Box
          component="button"
          onClick={onToggleMap}
          sx={{
            ...V2_EYEBROW,
            fontSize: "0.75rem",
            cursor: "pointer",
            background: mapOpen ? extraColors.translucentActive : "transparent",
            border: `1px solid ${
              mapOpen ? V2_ACCENT.cyan : V2_SURFACE.hairlineStrong
            }`,
            color: mapOpen ? extraColors.white : extraColors.translucent50,
            borderRadius: "6px",
            px: 1.5,
            py: 0.75,
            "&:hover": {
              color: extraColors.white,
              borderColor: V2_ACCENT.cyan,
            },
          }}
        >
          {mapOpen ? "◀ Hide scan map" : "▶ Show scan map"}
        </Box>
      </Block>

      <Block>
        <InfoHint context={filterContext} />
        <Box sx={{ display: "flex", gap: 4 }}>
          {isGdelt
            ? V2_SEVERITY_ORDER.map(tier => (
                <StatTooltip
                  key={tier}
                  context={filterContext}
                  description={`${V2_SEVERITY_LABEL[tier]} severity — clusters with impact ≥ ${V2_SEVERITY_MINS[tier]}`}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        backgroundColor: V2_SEVERITY_COLOR[tier],
                        boxShadow: `0 0 6px ${V2_SEVERITY_COLOR[tier]}`,
                      }}
                    />
                    <Typography
                      sx={{
                        ...V2_NUMERIC,
                        fontSize: "1.05rem",
                        fontWeight: 800,
                        color: extraColors.white,
                      }}
                    >
                      {sev[tier]}
                    </Typography>
                    <Eyebrow>{V2_SEVERITY_LABEL[tier]}</Eyebrow>
                  </Box>
                </StatTooltip>
              ))
            : NWSS_STATUS_ORDER.map(key => (
                <StatTooltip
                  key={key}
                  context={filterContext}
                  description={`${V2_STATUS_LABEL[key]} wastewater detection events`}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        backgroundColor: V2_STATUS_COLOR[key],
                        boxShadow: `0 0 6px ${V2_STATUS_COLOR[key]}`,
                      }}
                    />
                    <Typography
                      sx={{
                        ...V2_NUMERIC,
                        fontSize: "1.05rem",
                        fontWeight: 800,
                        color: extraColors.white,
                      }}
                    >
                      {status[key]}
                    </Typography>
                    <Eyebrow>{V2_STATUS_LABEL[key]}</Eyebrow>
                  </Box>
                </StatTooltip>
              ))}
        </Box>
      </Block>

      <Block>
        <InfoHint context={filterContext} />
        <Box sx={{ display: "flex", gap: 5 }}>
          {metrics.map(metric => (
            <StatTooltip
              key={metric.label}
              context={filterContext}
              description={metric.description}
            >
              <Box>
                <Metric value={metric.value} label={metric.label} />
              </Box>
            </StatTooltip>
          ))}
        </Box>
      </Block>

      <Box sx={{ flex: 1 }} />
    </Box>
  )
}
